import { useCallback, useMemo, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { Alert, Divider, IconButton, Stack, Tooltip } from '@mui/material'
import { GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'

import { ControlledTextField, FplDataGrid, FplDialog } from 'components/common'
import { ManualDriverInvoice } from 'constants/index'
import {
  GetManualDriverInvoiceCandidatesQuery,
  ManualDriverInvoiceBatchItemDto,
  ManualDriverInvoiceCandidateDto,
  useGenerateManualDriverInvoicesMutation,
} from 'generated/graphql'
import {
  GET_PAGED_DRIVER_INVOICES,
  GET_PAGED_MANUAL_DRIVER_INVOICE_CANDIDATES,
} from 'graphql/queries'
import { resetPagination } from 'helpers'
import { useGetManualDriverInvoiceCandidatesColumns } from 'hooks/fplDataGridColumns/useGetManualDriverInvoiceCandidatesColumns'

import { GenerateInvoiceConfirmDialog } from './GenerateInvoiceConfirmDialog'
import { GenerateInvoiceResults } from './GenerateInvoiceResults'

interface IProps {
  driverId: number
  open: boolean
  onClose: () => void
}

export const GenerateInvoiceDialog = (props: IProps) => {
  const { driverId, open, onClose } = props

  const client = useApolloClient()
  const columns = useGetManualDriverInvoiceCandidatesColumns()

  const [submittedSearch, setSubmittedSearch] = useState('')
  const [candidates, setCandidates] = useState<Array<ManualDriverInvoiceCandidateDto>>([])
  const [selectedCandidates, setSelectedCandidates] = useState<
    Array<ManualDriverInvoiceCandidateDto>
  >([])
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [results, setResults] = useState<Array<ManualDriverInvoiceBatchItemDto>>([])
  // Queued in this session but maybe not processed yet, so they cannot be queued twice
  const [queuedIds, setQueuedIds] = useState<Array<number>>([])

  const { control, reset, handleSubmit } = useForm({ defaultValues: { search: '' } })

  const searchValue = useWatch({ control, name: 'search' })

  const [generateInvoices, { loading }] = useGenerateManualDriverInvoicesMutation()

  const queryVariables = useMemo(
    () => ({ driverId, search: submittedSearch }),
    [driverId, submittedSearch],
  )

  const selectionModel = useMemo(
    () => selectedCandidates.map((candidate) => candidate.id),
    [selectedCandidates],
  )

  const isOverBatchLimit = selectedCandidates.length > ManualDriverInvoice.MAX_BATCH_SIZE

  const clearSelectionAndResults = () => {
    setSelectedCandidates([])
    setResults([])
  }

  const handleSearch = handleSubmit(({ search }) => {
    setSubmittedSearch(search.trim())
    clearSelectionAndResults()
  })

  const handleClearSearch = () => {
    reset()
    setSubmittedSearch('')
    clearSelectionAndResults()
  }

  const handleClose = () => {
    handleClearSearch()
    setQueuedIds([])
    resetPagination(ManualDriverInvoice.CANDIDATES_ENTITY_NAME)
    onClose()
  }

  const handleDataLoadComplete = useCallback((data: GetManualDriverInvoiceCandidatesQuery) => {
    setCandidates(data.pagedManualDriverInvoiceCandidates?.edges?.map((edge) => edge.node) ?? [])
  }, [])

  // Selected rows from other pages are kept, so look them up in the previous selection too
  const handleSelectionModelChange = (newSelectionModel: GridRowSelectionModel) => {
    setSelectedCandidates((previous) => {
      const knownCandidates = [...previous, ...candidates]

      return newSelectionModel
        .map((id) => knownCandidates.find((candidate) => candidate.id === id))
        .filter((candidate): candidate is ManualDriverInvoiceCandidateDto => !!candidate)
    })
  }

  const isRowSelectable = (params: GridRowParams<ManualDriverInvoiceCandidateDto>) =>
    !params.row.accountPayableRecordId && !queuedIds.includes(params.row.id)

  const handleConfirm = () => {
    generateInvoices({
      variables: { bookingDriverIds: selectedCandidates.map((candidate) => candidate.id) },
    })
      .then((result) => {
        const items = result.data?.generateManualDriverInvoices ?? []
        const queuedItems = items.filter((item) => item.isQueued)

        setResults(items)
        setSelectedCandidates([])
        setQueuedIds((previous) => [
          ...previous,
          ...queuedItems.map((item) => item.bookingDriverId),
        ])

        if (queuedItems.length === items.length) {
          toast.success(`${queuedItems.length} booking(s) queued for invoice and APR generation.`)
        } else {
          toast.warning(
            `${queuedItems.length} of ${items.length} booking(s) queued. See the results for details.`,
          )
        }
      })
      // The error message is shown by the Apollo error link
      .catch(() => undefined)
      .finally(() => {
        setIsConfirmOpen(false)
        client.refetchQueries({
          include: [GET_PAGED_DRIVER_INVOICES, GET_PAGED_MANUAL_DRIVER_INVOICE_CANDIDATES],
        })
      })
  }

  return (
    <>
      <FplDialog
        id='generate-invoice-dialog'
        open={open}
        caption='Generate Invoice'
        submitCaption={
          selectedCandidates.length
            ? `Generate Invoice (${selectedCandidates.length})`
            : 'Generate Invoice'
        }
        onCloseDialog={handleClose}
        onSaveClick={() => setIsConfirmOpen(true)}
        loading={loading}
        disabled={!selectedCandidates.length || isOverBatchLimit}
        fullWidth>
        <Stack spacing={2} pt={1}>
          <form autoComplete='off' onSubmit={handleSearch}>
            <ControlledTextField
              control={control}
              name='search'
              label='Search by FPL number, CX ID, tariff or job cost'
              endAdornment={
                <Stack direction='row' alignItems='center'>
                  {searchValue && (
                    <Tooltip title='Clear search'>
                      <IconButton
                        size='small'
                        aria-label='Clear search'
                        onClick={handleClearSearch}>
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Divider orientation='vertical' flexItem sx={{ mx: 1 }} />
                  <Tooltip title='Search eligible bookings'>
                    <span>
                      <IconButton
                        type='submit'
                        color='primary'
                        aria-label='Search'
                        disabled={!searchValue.trim()}>
                        <SearchIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              }
            />
          </form>

          {results.length > 0 && <GenerateInvoiceResults items={results} />}

          <Alert severity='info'>
            Bookings that already have an invoice and APR, or were just queued, cannot be selected.
          </Alert>

          {isOverBatchLimit && (
            <Alert severity='warning'>
              A maximum of {ManualDriverInvoice.MAX_BATCH_SIZE} bookings can be generated at once.
            </Alert>
          )}

          <FplDataGrid
            query={GET_PAGED_MANUAL_DRIVER_INVOICE_CANDIDATES}
            entityName={ManualDriverInvoice.CANDIDATES_ENTITY_NAME}
            queryVariables={queryVariables}
            skipQuery={!submittedSearch}
            columns={columns}
            defaultOrder={{ field: 'bookingId', sort: 'desc' }}
            checkboxSelection
            keepNonExistentRowsSelected
            isRowSelectable={isRowSelectable}
            selectionModel={selectionModel}
            onSelectionModelChange={handleSelectionModelChange}
            onDataLoadCompleteAction={handleDataLoadComplete}
            noRowsText={
              submittedSearch
                ? 'No eligible bookings found.'
                : 'Search to display eligible bookings.'
            }
            pageOptions={{
              pageSize: 5,
              rowsPerPage: [5, 10, 50],
            }}
            toolbar={{ caption: 'Eligible Bookings' }}
          />
        </Stack>
      </FplDialog>

      <GenerateInvoiceConfirmDialog
        open={isConfirmOpen}
        loading={loading}
        candidates={selectedCandidates}
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirm}
      />
    </>
  )
}
